import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { ErrorOutlineOutlined } from '@mui/icons-material';
import { Grid } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { STOItemStatusEnum } from 'commons/enums';
import * as colors from 'commons/styles/colors';
import { ISTOPickItemWithSummary } from 'commons/types';
import {
  getOnProgressSTOCheckBasket,
  stoCompleteChecking,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { STO_CHECK_ROUTES } from 'routes/constants';

import { STOSkuList } from './components/STOSkuList.component';
import { STOCheckSummaryPageStyle as S } from './STOCheckSummaryPage.style';

export const STOCheckSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { stoCheckBasket } = useSelector(
    (state: RootReducerInterface) => state.stoCheckBasket,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);

  useEffect(() => {
    const stoNumber = searchParams.get('stoNumber') ?? '';
    const basket = parseInt(searchParams.get('basket') ?? '0', 10) ?? 0;
    if (stoNumber === '' || basket === 0) {
      navigate(STO_CHECK_ROUTES.CHECK_BASKET);
      return;
    }
    if (!stoCheckBasket) {
      dispatch(getOnProgressSTOCheckBasket({ stoNumber, basket })).then(() =>
        setDataFetched(true),
      );
    }
  }, [stoCheckBasket, dispatch]);

  const { completeSku, incompleteSku } = useMemo(() => {
    function _skuSort(
      sku1: ISTOPickItemWithSummary,
      sku2: ISTOPickItemWithSummary,
    ) {
      // sort by quantity
      if (
        (sku1.checking_metadata.total_qty_checked || 0) <
        (sku2.checking_metadata.total_qty_checked || 0)
      )
        return -1;
      if (
        (sku1.checking_metadata.total_qty_checked || 0) >
        (sku2.checking_metadata.total_qty_checked || 0)
      )
        return 1;

      // sort by sku code
      return sku1.sku_code < sku2.sku_code ? -1 : 1;
    }
    const _completeSku: ISTOPickItemWithSummary[] = [];
    const _incompleteSku: ISTOPickItemWithSummary[] = [];

    if (stoCheckBasket) {
      stoCheckBasket.items.forEach((stoItem) => {
        const pickedQty = stoItem.pick_actual.reduce((total, { quantity }) => {
          return total + quantity;
        }, 0);

        if (
          stoItem.pick_actual.every((pickActual) => pickActual.check_passed) &&
          stoItem.checking_metadata.total_qty_checked &&
          stoItem.checking_metadata.total_qty_checked === pickedQty &&
          stoItem.checking_metadata.total_qty_checked === stoItem.quantity &&
          stoItem.box_number &&
          stoItem.pallet
        ) {
          _completeSku.push({ ...stoItem, pickedQty });
        } else {
          _incompleteSku.push({ ...stoItem, pickedQty });
        }
      });
    }

    return {
      completeSku: _completeSku.sort(_skuSort),
      incompleteSku: _incompleteSku.sort(_skuSort),
    };
  }, [stoCheckBasket]);

  if (!stoCheckBasket && dataFetched) navigate(STO_CHECK_ROUTES.CHECK_BASKET);
  if (!stoCheckBasket) return <div />;
  if (
    stoCheckBasket.items.some(
      (item) => item.status !== STOItemStatusEnum.CHECKING,
    )
  ) {
    navigate(STO_CHECK_ROUTES.CHECK_BASKET);
    return <div />;
  }

  const handleOpenDialog = () => setOpenModal(true);

  const handleCloseDialog = () => setOpenModal(false);

  const onConfirm = () => {
    dispatch(
      stoCompleteChecking({ navigate, stoNumber: stoCheckBasket.sto_number }),
    );
  };

  const prevPageHandler = () => {
    navigate({
      pathname: STO_CHECK_ROUTES.CHECK_BASKET,
      search: createSearchParams({
        stoNumber: stoCheckBasket.sto_number,
      }).toString(),
    });
  };

  return (
    <div>
      <S.ChildContentWrapper>
        {/* incomplete */}
        {incompleteSku.length > 0 && (
          <STOSkuList
            stoItems={incompleteSku}
            headerText="Incomplete Stock Transfer"
            headerColor={colors.PRIMARY_YELLOW}
            headerIcon={<ErrorOutlineOutlined />}
            defaultExpanded
          />
        )}

        {/* complete */}
        {completeSku.length > 0 && (
          <STOSkuList
            stoItems={completeSku}
            headerText="Complete Stock Transfer"
          />
        )}
        <S.BlankWhiteSpace />
      </S.ChildContentWrapper>

      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={6}>
            <S.SecondaryButton
              onClick={prevPageHandler}
              data-testid="BtnSummaryGoBack"
            >
              GO BACK
            </S.SecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <S.PrimaryButton
              onClick={handleOpenDialog}
              disabled={incompleteSku.length > 0}
              data-testid="BtnSummaryComplete"
            >
              COMPLETE TASK
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>

      <ConfirmationDialog
        title="Complete Task Confirmation"
        content="Are you sure to complete task?"
        onClose={() => handleCloseDialog()}
        openModal={openModal}
        onConfirm={onConfirm}
        dataTestIdOnClose="BtnSTOCheckSummaryPackNo"
        dataTestIdOnConfirm="BtnSTOCheckSummaryPackNo"
      />
    </div>
  );
};
